'use client';

import { useMemo } from 'react';
import { useTranslations } from 'next-intl';

import type {
  DynamicProgressBarSchema,
  ProgressBarSection,
} from '@workspace/lib/types/progress-bar-types';
import { getProgressBarUrls } from '@workspace/lib/utils/get-progress-bar-urls';
import { fromUpperToKebabCase } from '@workspace/lib/utils/string-utils';

export interface StepState {
  isCompleted: boolean;
  isActive: boolean;
  isFuture?: boolean;
}

export const ProgressBar = ({
  progressBarSchema,
  shouldIncludeLastStep = false,
}: {
  progressBarSchema?: DynamicProgressBarSchema;
  shouldIncludeLastStep?: boolean;
}) => {
  const { sections, activeUrl } = progressBarSchema || {
    sections: [],
    activeUrl: null,
  };

  const t = useTranslations();

  const stepStates = useMemo(() => {
    if (!sections || sections.length === 0) return [];

    let activeSectionIndex: number | null = null;
    let activeSubSectionIndex: number | null = null;

    const urlWithRemovedTrailingSlashes = activeUrl?.replace(/\/$/, '') ?? '';
    // Note: handle cases like offer types (with capital letters and underscores)
    const normalizedActiveUrl =
      fromUpperToKebabCase(urlWithRemovedTrailingSlashes) || null;

    for (let i = 0; i < sections.length; i++) {
      const section = sections[i];

      if (section.subSections && section.subSections.length > 0) {
        for (let j = 0; j < section.subSections.length; j++) {
          if (
            getProgressBarUrls(section.subSections[j]).some(
              (url) => url.replace(/\/$/, '') === normalizedActiveUrl,
            )
          ) {
            activeSectionIndex = i;
            activeSubSectionIndex = j;
            break;
          }
        }
        if (activeSectionIndex !== null) break;
      }

      const sectionUrls = getProgressBarUrls(section);
      if (sectionUrls.length) {
        const normalizedSectionUrls = sectionUrls.map((url) =>
          url.replace(/\/$/, ''),
        );

        if (
          normalizedActiveUrl &&
          normalizedSectionUrls.includes(normalizedActiveUrl)
        ) {
          activeSectionIndex = i;
          activeSubSectionIndex = null;
          break;
        }
      }
    }

    const states: (StepState & {
      section: ProgressBarSection;
      subSectionStates?: StepState[];
    })[] = [];

    for (let sectionIndex = 0; sectionIndex < sections.length; sectionIndex++) {
      const section = sections[sectionIndex];
      const subSectionStates: StepState[] = [];
      let sectionIsActive = false;
      let sectionHasActiveSubSection = false;

      if (section.subSections && section.subSections.length > 0) {
        for (
          let subIndex = 0;
          subIndex < section.subSections.length;
          subIndex++
        ) {
          const isActive =
            activeSectionIndex === sectionIndex &&
            activeSubSectionIndex === subIndex;
          const isCompleted =
            activeSectionIndex !== null &&
            (sectionIndex < activeSectionIndex ||
              (sectionIndex === activeSectionIndex &&
                subIndex < (activeSubSectionIndex ?? -1)));

          if (isActive) {
            sectionIsActive = true;
            sectionHasActiveSubSection = true;
          }

          subSectionStates.push({ isCompleted, isActive });
        }
      }

      if (!sectionHasActiveSubSection) {
        sectionIsActive =
          activeSectionIndex === sectionIndex && activeSubSectionIndex === null;
      }

      const sectionIsCompleted =
        activeSectionIndex !== null && sectionIndex < activeSectionIndex;

      states.push({
        section,
        isCompleted: sectionIsCompleted,
        isActive: sectionIsActive,
      });
    }

    return states;
  }, [sections, activeUrl]);

  if (!sections || sections.length === 0) {
    return null;
  }

  const totalSteps = shouldIncludeLastStep
    ? stepStates.length
    : stepStates.length - 1;
  const lastFilledIndex = stepStates.reduce(
    (acc, s, i) => (s.isCompleted || s.isActive ? i : acc),
    -1,
  );
  const filledCount = lastFilledIndex + 1;
  const filledPercent = totalSteps > 0 ? (filledCount / totalSteps) * 100 : 0;
  const sectionTitle = sections[lastFilledIndex]?.title;
  const translatedSectionTitle = sectionTitle
    ? t.has(sectionTitle)
      ? t(sectionTitle)
      : sectionTitle
    : '';

  return (
    <div className='flex flex-1 justify-center sm:max-w-3xl lg:max-w-2xl lg:justify-start'>
      <div className='flex w-full flex-col gap-[3px] md:gap-2'>
        <div className='relative h-1 w-full overflow-hidden rounded-full bg-neutral-600'>
          <div
            className='bg-provi-gradient absolute top-0 bottom-0 left-0'
            style={{ width: `${filledPercent}%` }}
          />
        </div>

        <div className='mb-4 w-full md:mb-8'>
          <span className='text-black-primary text-xs md:text-sm'>
            <span>
              {t('ProgressBar.steps', {
                current: filledCount,
                total: totalSteps,
              })}
            </span>

            {translatedSectionTitle && (
              <span className='ml-2 font-bold'>{translatedSectionTitle}</span>
            )}
          </span>
        </div>
      </div>
    </div>
  );
};
